import { useEffect, useMemo, useRef } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import cm from 'classnames';

import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useLocation, useAppNavigator, Path } from 'components/_hooks/use-router';
import { passStoryAccessedAt, passProjectAccessedAt } from 'components/app/_mediator/app-data.slice';
import { useSbView } from 'components/app/storyboard/_mediator/use-sb-view';

import { AudioTrack } from 'components/app/audio-track/audio-track';
import { FinalcutMenu } from 'components/app/finalcuts/finalcuts-list/finalcuts-list-element-menu';
import { FinalcutTrack } from 'components/app/finalcuts/finalcuts-track/finalcuts-track';
import { MediaListMenu } from 'components/app/selector/selector-files/elements/selector-files-menu';
import { SbTracks } from 'components/app/storyboard/sb-tracks/sb-tracks';
import { TrackClipMenu } from 'components/app/app-components/track-clip/clip-menu';
import { AppLayoutSide } from './app-layout-side';
import { AppLayoutSb } from './app-layout-sb';
import { AppLayoutFc } from './app-layout-fc';
import { AppLayoutSr } from './app-layout-sr';
import styles from './_app-layout.module.scss';

export const AppLayout = () => {
  const { path } = useLocation();
  const { navigateTo } = useAppNavigator();
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const configPanel = useAppSelector((state) => state.editor.configPanel);

  const appRestored = useAppSelector((state) => state.app.appRestored);
  const rootRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const sbViewClassNames = useSbView(rootRef, styles);
  const pathClassName = useMemo(() => {
    if (path === Path.selector) return styles['Layout--selector'];
    if (path === Path.storyboard) return styles['Layout--storyboard'];
    if (path === Path.finalcuts) return styles['Layout--finalcuts'];
  }, [path]);

  useEffect(() => {
    if (!appRestored) return;
    if (!selectedStory) {
      navigateTo(Path.app);
      return;
    }
  }, [appRestored]);

  useEffect(() => {
    if (!selectedProject || !selectedStory) return;
    dispatch(passStoryAccessedAt(selectedStory));
    dispatch(passProjectAccessedAt(selectedProject));
  }, [selectedProject, selectedStory]);

  return (
    <div
      ref={rootRef}
      className={cm(styles.Layout, pathClassName, sbViewClassNames, {
        [styles['Layout--side']]: configPanel,
      })}
    >
      <div className={styles.Layout__top}>
        <Switch>
          <Route exact path={Path.selector} component={AppLayoutSr} />
          <Route exact path={Path.storyboard} component={AppLayoutSb} />
          <Route exact path={Path.finalcuts} component={AppLayoutFc} />
          <Redirect to={Path.app} />
        </Switch>
      </div>
      <div className={styles.Layout__bottom}>
        <AudioTrack className={styles.Layout__audio} />
        <div className={styles.Layout__tracks}>
          <Switch>
            <Route exact path={Path.storyboard} component={SbTracks} />
            <Route exact path={Path.finalcuts} component={FinalcutTrack} />
          </Switch>
        </div>
      </div>
      <AppLayoutSide />
      <TrackClipMenu />
      <MediaListMenu />
      <FinalcutMenu />
    </div>
  );
};
